import { useCallback, useEffect, useState } from 'react';

type Options = {
  enabled?: boolean;
  onKeyDown?: (e: KeyboardEvent) => void;
  onKeyUp?: (e: KeyboardEvent) => void;
};

/**
 * Observe keyboard interactions for a specific key.
 * @param {string} targetKey The keyboard key to observe, this comes from the KeyboardEvent.key value
 * @param {Object} options additional configuration object
 * @param {boolean} options.enabled whether the key event listener is enabled, defaults to true
 * @param {requestCallback} options.onDown fired when the key is pressed down
 * @param {requestCallback} options.onUp fired when the key is released up
 * @returns isPressed a boolean for currently pressed state
 * @example
 * import { useKeyEvent } from '@adsk/alloy-react-helpers';
 *
 * const { isPressed } = useKeyEvent('Escape', {
 *   onKeyDown: console.log,
 *   onKeyUp: console.log
 * });
 */
export default function useKeyEvent(
  targetKey: KeyboardEvent['key'],
  { enabled = true, onKeyDown, onKeyUp }: Options = {},
) {
  const [isPressed, setIsPressed] = useState<boolean>(false);
  const downHandler = useCallback(
    (e: KeyboardEvent) => {
      if (e.key === targetKey) {
        setIsPressed(true);
        onKeyDown?.(e);
      }
    },
    [targetKey, onKeyDown],
  );

  const upHandler = useCallback(
    (e: KeyboardEvent) => {
      if (e.key === targetKey) {
        setIsPressed(false);
        onKeyUp?.(e);
      }
    },
    [targetKey, onKeyUp],
  );

  useEffect(() => {
    if (enabled) {
      document.addEventListener('keydown', downHandler, false);
      document.addEventListener('keyup', upHandler, false);
    }

    return () => {
      document.removeEventListener('keydown', downHandler, false);
      document.removeEventListener('keyup', upHandler, false);
    };
  }, [downHandler, enabled, upHandler]);
  return { isPressed };
}
